import React, { Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';

// project import
import Loader from './common/Loader';
import { useOnCall } from 'hooks/useOnCall';

// A screen whose bundle is gone after a deploy reloads the tab once into the new release. A reload in
// the last 30 s means the new release fails too, so the tab shows an error instead of looping.
const CHUNK_RELOAD_KEY = 'chunkLoadReloadAt';
const CHUNK_RELOAD_GUARD_MS = 30 * 1000;

const isChunkLoadError = error =>
    error?.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message);

const reloadedRecently = () => {
    try {
        return (
            Date.now() - Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) <
            CHUNK_RELOAD_GUARD_MS
        );
    } catch (error) {
        return true;
    }
};

const reload = () => {
    try {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch (error) {
        // Without storage there is no loop guard, so the tab is not reloaded automatically.
        return;
    }
    window.location.reload();
};

// Never reloads during a call: it waits until the call ends.
const ChunkLoadFallback = () => {
    const onCall = useOnCall();
    const canReload = !reloadedRecently();

    useEffect(() => {
        if (!onCall && canReload) reload();
    }, [onCall, canReload]);

    if (!onCall && canReload) return <Loader />;

    return (
        <Box sx={{ py: 8, px: 2, display: 'flex', justifyContent: 'center' }}>
            <Stack
                spacing={1.5}
                alignItems="center"
                sx={{ maxWidth: 420, textAlign: 'center' }}
            >
                <Typography variant="h4">Update needed to open this screen</Typography>
                <Typography color="textSecondary">
                    {onCall
                        ? 'A new version of SAGE is available. This screen opens after your call, once the page restarts.'
                        : "This screen couldn't load. Restart to try again."}
                </Typography>
                {!onCall && (
                    <Button variant="contained" onClick={() => window.location.reload()}>
                        Restart
                    </Button>
                )}
            </Stack>
        </Box>
    );
};

class ChunkLoadBoundary extends React.Component {
    state = { error: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    render() {
        const { error } = this.state;
        // Anything else goes on to the next boundary up, as before.
        if (error && !isChunkLoadError(error)) throw error;
        if (error) return <ChunkLoadFallback />;
        return this.props.children;
    }
}

ChunkLoadBoundary.propTypes = {
    children: PropTypes.node
};

// ==============================|| LOADABLE - LAZY LOADING ||============================== //

const Loadable = Component => {
    const LoadableComponent = props => (
        <ChunkLoadBoundary>
            <Suspense fallback={<Loader />}>
                <Component {...props} />
            </Suspense>
        </ChunkLoadBoundary>
    );

    LoadableComponent.displayName = `Loadable(${
        Component.displayName || Component.name || 'Component'
    })`;

    return LoadableComponent;
};

export default Loadable;
